/**
 * Utilities for building flow nodes and edges from configuration
 */

import { Edge, MarkerType, Node, Position } from '@xyflow/react';
import { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';
import { DialogState, EntryNodeOverride, FlowDefinition, KeyedItem, FlowNodeConfig } from '../types';

const ENTRY_NODE_ID = 'n1';
import { extractItemMetadata, getByPath, pickFirstString, unwrap, MetaDoc, ValueWrapper } from './metadata';

/**
 * Generate unique key for each item
 */
export function getItemKey(item: ObservabilityLogsRetrieve200ResultsItem, index: number): string {
  const anyItem = item as unknown as Record<string, unknown>;
  const preferred = anyItem['id'] ?? anyItem['message_id'] ?? anyItem['created_at'];
  const base = preferred != null && preferred !== '' ? String(preferred) : '';
  return `k-${index}-${base}`;
}

// ============================================================================
// Node Detection Helpers
// ============================================================================

/**
 * Check if item has compliance/filtering activity.
 *
 * The compliance agent (n2) should show whenever filtering was performed,
 * regardless of the action (ALLOW, WARN, AMEND, BLOCK).
 *
 * Key indicators:
 * - filter_response exists (main text filtering)
 * - attachment_results exists (file filtering)
 * - Any action field related to filtering
 */
function hasCompliance(md: MetaDoc): boolean {
  // Check for any compliance action indicators
  const action = pickFirstString(md, [
    'filter_response.value.action',
    'filter_response.action',
    'overall_action',
    'overall_action.value',
    'overall_action_early',
    'overall_action_early.value',
    'attachments_action',
    'attachments_action.value',
  ]);

  // Skip if action is ONLY "BYPASSED" (no actual compliance check occurred)
  if (action === 'BYPASSED') {
    return false;
  }

  // Check for main filter response
  const filterResponse = getByPath(md, 'filter_response.value') || getByPath(md, 'filter_response');

  // Check for attachment filtering
  const attachmentResults = getByPath(md, 'attachment_results.value') || getByPath(md, 'attachment_results');

  // Compliance node should show if ANY filtering occurred
  return !!(filterResponse || attachmentResults || action);
}

/**
 * Check if item has user interaction (button click, manual edit, etc.)
 *
 * Only counts ACTUAL user actions, not system states like "BYPASSED".
 * Valid user actions: edit_message, accept_changes, override_submit, attachment_decisions
 */
function hasUserInteraction(md: MetaDoc): boolean {
  const actionType = pickFirstString(md, ['prompt_decision_received', 'user_action', 'user_action.value']);

  // Check for prompt-level decisions
  const isValidUserAction =
    actionType && ['edit_message', 'accept_changes', 'override_submit', 'attachment_decisions'].includes(actionType);

  // Also check if attachment_decisions metadata exists (user made attachment choices)
  const attachmentDecisions = getByPath(md, 'attachment_decisions.value') as Record<string, string> | undefined;
  const hasAttachmentDecisions = attachmentDecisions && Object.keys(attachmentDecisions).length > 0;

  return !!(isValidUserAction || hasAttachmentDecisions);
}

/**
 * Check if item is a follow-up to a previous message (2nd+ in a chain).
 * This is detected by the presence of linked_to_user_message_id.
 */
function isLinkedFollowUp(md: MetaDoc): boolean {
  const linkedId = pickFirstString(md, ['linked_to_user_message_id', 'linked_to_user_message_id.value']);
  return !!linkedId;
}

/**
 * Check if item has routing/agent selection activity
 */
function hasRouting(md: MetaDoc): boolean {
  const routerFlag = unwrap<boolean>(
    getByPath(md, 'router_question_path') as ValueWrapper<boolean> | boolean | undefined
  );
  const agentName = pickFirstString(md, ['agent_name', 'agent_name.value', 'agent_version', 'agent_version.value']);
  const hasSystem = pickFirstString(md, ['system_prompt_content', 'safe_system_prompt']);

  // Check if routerFlag is explicitly TRUE (not just defined; false means bot was not engaged)
  return routerFlag === true || !!agentName || !!hasSystem;
}

/**
 * Check if item has agent execution activity (tool calls, etc.)
 */
function hasExecution(md: MetaDoc): boolean {
  const toolCalls = getByPath(md, 'tool_call_details.value') as unknown[] | undefined;
  const usedTools = getByPath(md, 'used_tools.value') as unknown[] | undefined;
  const filesForChat = getByPath(md, 'files_for_chat.value') as unknown[] | undefined;
  const agentName = pickFirstString(md, ['agent_name', 'agent_name.value']);

  return (
    (Array.isArray(toolCalls) && toolCalls.length > 0) ||
    (Array.isArray(usedTools) && usedTools.length > 0) ||
    (Array.isArray(filesForChat) && filesForChat.length > 0) ||
    !!agentName
  );
}

/**
 * Check if item has AI response text
 */
function hasResponse(md: MetaDoc): boolean {
  const responseText = pickFirstString(md, [
    'response_text',
    'response_text.value',
    'clean_text',
    'clean_text.value',
    'text',
    'assistant_response',
    'answer',
  ]);

  return !!responseText;
}

/**
 * Determine which nodes should be included for a specific log item.
 *
 * Key distinction:
 * - Normal flow (1st message): starts with n1 (User Message)
 * - Follow-up flow (2nd+ message): starts with n4 (User Interaction), skips n1 and n3
 *
 * This enables proper visualization of edit/decision chains:
 * - Log 1 (Original): n1 → n2 (AMEND) → n3 (warning)
 * - Log 2 (User acted): n4 (action) → n2 (ALLOW) → n5 (routing) → n7 (response)
 * - Log 3+ (Further actions): same as Log 2 pattern
 */
export function getIncludedNodeIds(
  item: ObservabilityLogsRetrieve200ResultsItem,
  _allNodeConfigs: FlowNodeConfig[]
): string[] {
  const md = extractItemMetadata(item) as MetaDoc;

  // Detect if this is a follow-up message (2nd+ in chain)
  // Indicated by linked_to_user_message_id OR meaningful user action
  const isFollowUp = isLinkedFollowUp(md) || hasUserInteraction(md);

  const included: string[] = [];

  if (isFollowUp) {
    // Follow-up flow: starts with user interaction (2nd+ message in chain)
    // User clicked button, manually edited, or system linked to previous message

    // n4: User Interaction - always show for follow-ups (this is the starting point)
    included.push('n4');

    // n2: Compliance Agent - show if filtering occurred
    if (hasCompliance(md)) {
      included.push('n2');
    }

    // Skip n3 (Compliance Message) - follow-ups don't show UI warnings again

    // n5: Routing Agent - show if routing occurred
    if (hasRouting(md)) {
      included.push('n5');
    }

    // n6: Agent Execution - show if tools were used
    if (hasExecution(md)) {
      included.push('n6');
    }

    // n7: AI Response - show if response was generated
    if (hasResponse(md)) {
      included.push('n7');
    }
  } else {
    // Normal flow: starts with user message (1st message in potential chain)
    // This is either a standalone message OR the FIRST log entry before user action

    // n1: User Message - always show for initial messages
    included.push('n1');

    // n2: Compliance Agent - show if filtering occurred (merged n2+n3 functionality)
    if (hasCompliance(md)) {
      included.push('n2');
    }

    // Skip n3 - merged into n2

    // n4: User Interaction - show if user made a decision (accept/proceed/etc.)
    // NOTE: This should NOT trigger for "BYPASSED" (system state, not user action)
    if (hasUserInteraction(md)) {
      included.push('n4');
    }

    // n5: Routing Agent - show if routing occurred
    if (hasRouting(md)) {
      included.push('n5');
    }

    // n6: Agent Execution - show if tools were used
    if (hasExecution(md)) {
      included.push('n6');
    }

    // n7: AI Response - show if response was generated
    if (hasResponse(md)) {
      included.push('n7');
    }
  }

  return included;
}

export function resolveFlowEdgeColor(
  sourceId: string,
  targetId: string,
  item: ObservabilityLogsRetrieve200ResultsItem,
  flowDef: FlowDefinition
): string {
  const md = extractItemMetadata(item);
  const defined = flowDef.edges.find((edge) => edge.sourceId === sourceId && edge.targetId === targetId);
  if (defined) {
    return defined.colorResolver(item, md);
  }
  return '#94a3b8';
}

/**
 * Build nodes from flow definition and keyed items.
 *
 * Each item gets its own row (yPosition), but only the nodes relevant to that item are created.
 * This is determined by getIncludedNodeIds() which looks at the item's metadata.
 *
 * CRITICAL IMPROVEMENTS:
 * 1. All nodes for a single item MUST be on the same horizontal line (same Y)
 * 2. X positions are DYNAMICALLY CALCULATED to evenly distribute nodes in each row
 * 3. This prevents gaps when some nodes are filtered out
 *
 * Example:
 * - Item 1 with [n1, n2, n3]: distributes evenly at x=0, x=300, x=600
 * - Item 2 with [n4, n2, n7]: distributes evenly at x=0, x=300, x=600 (no gaps!)
 *
 * The result is clean, evenly-spaced rows regardless of which nodes are present.
 */
export function buildNodesFromFlow(
  flowDef: FlowDefinition,
  keyedItems: KeyedItem[],
  yOffset = 150,
  entryNode?: EntryNodeOverride
): Node[] {
  const nodes: Node[] = [];
  const verticalSpacing = 150;
  const horizontalSpacing = 300; // Space between nodes in a row
  const startX = 0; // Starting X position for first node

  keyedItems.forEach(({ key, item }, idx) => {
    const y = yOffset + idx * verticalSpacing;

    // Determine which nodes this specific item should have (in correct order)
    const includedNodeIds = getIncludedNodeIds(item, flowDef.nodes);

    // Get node configs for included nodes, PRESERVING THE ORDER from getIncludedNodeIds
    // This is critical because manual edits need n4 BEFORE n2, but flow definition has n2 before n4
    const nodeConfigById = new Map(flowDef.nodes.map((nc) => [nc.id, nc]));
    const includedNodeConfigs = includedNodeIds
      .map((id) => nodeConfigById.get(id))
      .filter((nc): nc is FlowNodeConfig => nc !== undefined);

    // Create nodes with dynamically calculated X positions
    includedNodeConfigs.forEach((nodeConfig, positionIndex) => {
      const x = startX + positionIndex * horizontalSpacing;

      const isEntryNode = nodeConfig.id === ENTRY_NODE_ID && entryNode;

      const node: Node = {
        id: `${key}-${nodeConfig.id}`,
        position: {
          x: x, // Dynamically calculated based on position in row
          y: y, // Calculated based on item index
        },
        data: {
          title: isEntryNode && entryNode.title ? entryNode.title : nodeConfig.title,
          subtitle: isEntryNode && entryNode.subtitle !== undefined ? entryNode.subtitle : nodeConfig.subtitle,
          icon: isEntryNode && entryNode.icon !== undefined ? entryNode.icon : nodeConfig.icon,
        },
        type: 'appNode',
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      };
      nodes.push(node);
    });
  });

  return nodes;
}

/**
 * Build edges from flow definition and keyed items.
 *
 * Creates two types of edges:
 * 1. Within-row edges: Connect nodes in the same log entry
 * 2. Cross-row edges: Connect last node of row N to first node of row N+1 (when linked)
 *
 * Strategy:
 * - Use defined edges from flow if they exist between consecutive nodes
 * - Create "bridge" edges (neutral color) if no defined edge exists
 * - Cross-row edges use purple (#8b5cf6) to indicate user decision/action
 *
 * Example:
 * - Row 1 [n1, n2, n3]: n1→n2, n2→n3
 * - Cross-row: n3→n4 (purple, indicates user interaction)
 * - Row 2 [n4, n2, n5, n7]: n4→n2, n2→n5, n5→n7
 */
export function buildEdgesFromFlow(flowDef: FlowDefinition, keyedItems: KeyedItem[]): Edge[] {
  const edges: Edge[] = [];
  const makeMarker = (color: string) => ({
    type: MarkerType.ArrowClosed as const,
    color,
    width: 18,
    height: 18,
  });

  // Create edge lookup map
  const edgeMap = new Map<string, (typeof flowDef.edges)[0]>();
  flowDef.edges.forEach((edgeConfig) => {
    edgeMap.set(`${edgeConfig.sourceId}-${edgeConfig.targetId}`, edgeConfig);
  });

  // Step 1: Create within-row edges
  keyedItems.forEach(({ item, key }) => {
    const md = extractItemMetadata(item);

    // Get which nodes exist for this item (in order)
    const includedNodeIds = getIncludedNodeIds(item, flowDef.nodes);

    // Create edges between consecutive nodes in this row
    for (let i = 0; i < includedNodeIds.length - 1; i++) {
      const sourceId = includedNodeIds[i];
      const targetId = includedNodeIds[i + 1];
      const edgeKey = `${sourceId}-${targetId}`;

      // Check if there's a defined edge for this pair
      const definedEdge = edgeMap.get(edgeKey);

      let color: string;
      if (definedEdge) {
        // Use the color resolver from the defined edge
        color = definedEdge.colorResolver(item, md);
      } else {
        // Bridge edge - use neutral color
        color = '#94a3b8';
      }

      const edge: Edge = {
        id: `${key}-e-${sourceId}-${targetId}`,
        source: `${key}-${sourceId}`,
        target: `${key}-${targetId}`,
        style: { stroke: color },
        markerEnd: makeMarker(color),
      };
      edges.push(edge);
    }
  });

  // Step 2: Create cross-row edges (connect all consecutive rows)
  // Since we explode logs into separate rows, and they're in chronological order,
  // simply connect the last node of each row to the first node of the next row
  for (let i = 0; i < keyedItems.length - 1; i++) {
    const currentItem = keyedItems[i];
    const nextItem = keyedItems[i + 1];

    // Get last node of current row
    const currentNodeIds = getIncludedNodeIds(currentItem.item, flowDef.nodes);
    const lastNodeOfCurrent = currentNodeIds[currentNodeIds.length - 1];

    // Get first node of next row
    const nextNodeIds = getIncludedNodeIds(nextItem.item, flowDef.nodes);
    const firstNodeOfNext = nextNodeIds[0];

    if (lastNodeOfCurrent && firstNodeOfNext) {
      const edge: Edge = {
        id: `cross-${currentItem.key}-${nextItem.key}`,
        source: `${currentItem.key}-${lastNodeOfCurrent}`,
        target: `${nextItem.key}-${firstNodeOfNext}`,
        style: { stroke: '#8b5cf6', strokeWidth: 3 }, // Purple, thicker to indicate user action
        markerEnd: makeMarker('#8b5cf6'),
      };
      edges.push(edge);
    }
  }

  return edges;
}

/**
 * Build dialog state from node ID
 */
export function buildDialogForNode(
  nodeId: string,
  flowDef: FlowDefinition,
  keyedItems: KeyedItem[],
  entryNode?: EntryNodeOverride
): DialogState {
  // Expect nodeId in the format `${itemKey}-${nodeId}`
  const lastDashIndex = nodeId.lastIndexOf('-');
  if (lastDashIndex === -1) return null;

  const itemKey = nodeId.slice(0, lastDashIndex);
  const stage = nodeId.slice(lastDashIndex + 1);

  const pair = keyedItems.find((k) => k.key === itemKey);
  const item = pair?.item;
  if (!item) return null;

  const nodeConfig = flowDef.nodes.find((n) => n.id === stage);
  if (!nodeConfig) return null;

  const isEntryNode = stage === ENTRY_NODE_ID && entryNode;
  const title = isEntryNode && entryNode.title ? entryNode.title : nodeConfig.title;
  const extraFields = isEntryNode ? (entryNode.extraFields ?? []) : [];

  const md = extractItemMetadata(item);
  const fields = [...extraFields, ...nodeConfig.dialogBuilder(item, md)];

  // If no fields, show a friendly message instead of blocking the dialog
  if (!fields || fields.length === 0) {
    return {
      open: true,
      title,
      fields: [
        {
          label: 'Status',
          value: 'Not applicable for this log (bot was not addressed or no data captured for this stage).',
        },
      ],
    };
  }

  return {
    open: true,
    title,
    fields,
  };
}
